// The editor's optimistic picture of clips: what the tab draws while its own
// proposals are still on their way to the room. Pure, so the jam fuzzer's web
// peer (extension/test/fuzz/web-peer.ts) runs the same code useJamEditor does.
//
// An overlay is keyed by clip id and stamped with the send that wrote it. It
// is held while the send is in flight, given the room's answer (the rev it
// cites from then on, and the roomRev at which the replica will have caught
// up), and retired once the replica has applied that roomRev — the confirmed
// clip and the removal of its overlay land in the same render.

import type { Intent, IntentResponse } from "@shared/sync/intents.js";
import type { ClipData } from "@shared/sync/protocol.js";
import type { CanonicalRoom } from "@shared/sync/state.js";

/** A local change we've proposed but the room hasn't confirmed to us yet. */
export interface PendingEdit {
  patch: Partial<ClipData> & { trackId?: string };
  deleted: boolean;
  /** Which `send` wrote it. A response stamps `untilRoomRev` only on its
   * own overlay: a later edit to the same clip, already overlaid, must not
   * be retired when the EARLIER edit's broadcast lands. */
  seq: number;
  /** The rev the room gave the clip; null while in flight. A second edit
   * before the broadcast lands cites it instead of the stale canonical one
   * (a held arrow key used to be a stale rejection per repeat). */
  rev: number | null;
  /** Drop this once the replica has applied that rev. Null while in flight. */
  untilRoomRev: number | null;
}

export type Overlay = { clipId: string; patch: PendingEdit["patch"]; deleted: boolean };

export function overlayFor(intent: Intent, room: CanonicalRoom | null): Overlay | null {
  if (intent.kind === "deleteClip") return { clipId: intent.id, patch: {}, deleted: true };
  if (intent.kind === "upsertClip" && intent.id !== null) {
    // A note edit drops a MIDI clip's bounce the moment it is made, as the
    // room will (merge-engine `notesEdited`): what plays must not flip from
    // synth back to the stale bounce for the length of a round trip.
    const existing = room?.clips.find((c) => c.id === intent.id);
    const notesEdited =
      existing?.clipKind === "midi" && intent.clip.notes !== undefined && intent.clip.notesDigest !== existing.notesDigest;
    return {
      clipId: intent.id,
      patch: { ...intent.clip, trackId: intent.trackId, ...(notesEdited ? { audioKey: null } : {}) },
      deleted: false,
    };
  }
  // Creates have no id to key an overlay on, and inventing one would put a clip
  // on screen that the room might refuse. They appear when the room says so.
  return null;
}

/** The overlays a send makes, from the view it was built against. */
export function overlaysOf(intents: readonly Intent[], before: CanonicalRoom | null): Overlay[] {
  return intents.map((intent) => overlayFor(intent, before)).filter((o): o is Overlay => o !== null);
}

/** Send `seq` is on its way: its overlays are drawn. */
export function holdOverlays(prev: ReadonlyMap<string, PendingEdit>, overlays: readonly Overlay[], seq: number): Map<string, PendingEdit> {
  const next = new Map(prev);
  for (const o of overlays) next.set(o.clipId, { patch: o.patch, deleted: o.deleted, rev: null, untilRoomRev: null, seq });
  return next;
}

/** The room answered send `seq`: a refused edit never happened, an accepted
 * one waits for its broadcast. */
export function answerOverlays(
  prev: ReadonlyMap<string, PendingEdit>,
  overlays: readonly Overlay[],
  seq: number,
  result: IntentResponse,
): Map<string, PendingEdit> {
  const refused = new Set(result.rejected.map((r) => overlayFor(r.intent, null)?.clipId).filter((id): id is string => id !== undefined));
  const next = new Map(prev);
  for (const o of overlays) {
    const held = next.get(o.clipId);
    if (!held) continue;
    if (held.seq !== seq) {
      // A later edit to this clip is overlaid already: it cites the
      // rev this answer gave, and retires on ITS answer, not this one.
      next.set(o.clipId, { ...held, rev: result.roomRev });
      continue;
    }
    // A refused edit never happened: drop the overlay so the canonical
    // value shows through immediately.
    if (refused.has(o.clipId)) next.delete(o.clipId);
    else next.set(o.clipId, { ...held, rev: result.roomRev, untilRoomRev: result.roomRev });
  }
  return next;
}

/** Send `seq` got no answer: the guess it drew can't be defended. */
export function dropOverlays(prev: ReadonlyMap<string, PendingEdit>, overlays: readonly Overlay[], seq: number): Map<string, PendingEdit> {
  const next = new Map(prev);
  for (const o of overlays) if (next.get(o.clipId)?.seq === seq) next.delete(o.clipId);
  return next;
}

/** The overlays still drawn once the replica is at `appliedRoomRev`. */
export function liveOverlays(pending: ReadonlyMap<string, PendingEdit>, appliedRoomRev: number, room: CanonicalRoom | null): Map<string, PendingEdit> {
  const next = new Map<string, PendingEdit>();
  for (const [id, edit] of pending) {
    if (edit.untilRoomRev !== null && appliedRoomRev >= edit.untilRoomRev) continue;
    // Answered, and its clip is gone: nothing to draw over, and after a room
    // reset (which rewinds roomRev) it might wait a long time for its rev.
    if (edit.untilRoomRev !== null && room !== null && !room.clips.some((c) => c.id === id)) continue;
    next.set(id, edit);
  }
  return next;
}

/** Fold clip overlays over canonical clips. Deletes hide the clip, upserts
 * patch it — so a dragged clip stays where the user dropped it instead of
 * snapping back for the length of a round trip. */
export function projectClips(room: CanonicalRoom, pending: ReadonlyMap<string, PendingEdit>): CanonicalRoom["clips"] {
  if (pending.size === 0) return room.clips;
  const clips = [];
  for (const clip of room.clips) {
    const edit = pending.get(clip.id);
    if (!edit) {
      clips.push(clip);
      continue;
    }
    if (edit.deleted) continue;
    clips.push({ ...clip, ...edit.patch, ...(edit.rev !== null ? { rev: edit.rev } : {}) });
  }
  return clips;
}
